import { config } from 'enviroment.js'
import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Modal, Upload, message, Form, Input, Button, Popconfirm } from 'antd'
import {
  LoadingOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import 'antd/dist/antd.css'
import 'Pages/Admin/Admin.scss'
import { getCookie } from 'common/utils/auth'

const headers = {
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getCookie('token')}`,
}

function ScanItemModal(props) {
  const {
    visible,
    setVisible,
    activeItem,
    setActiveItem,
    setScans,
    experienceInfo,
  } = props
  const [loading, setLoading] = useState(false)
  const [thumbnailSrc, setThumbnailSrc] = useState()
  const [thumbnailFile, setThumbnailFile] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    if (activeItem) {
      // Parse data if it's a string, otherwise use directly
      let data = activeItem.data
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch (e) {
          data = {}
        }
      }

      form.setFieldsValue({
        scanId: activeItem?.id,
        url: data?.src || '',
        displayName: data?.name || '',
      })
      setThumbnailSrc(data?.thumbnail || '')
    }
  }, [form, activeItem])

  const onCancel = () => {
    setVisible(false)
    setActiveItem(null)
    setThumbnailSrc(null)
    setThumbnailFile(null)
    form.resetFields()
  }

  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  )

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!')
      return false
    }
    const isLt3M = file.size / 1024 / 1024 < 3
    if (!isLt3M) {
      message.error('Image must be smaller than 3MB!')
      return false
    }
    if (isJpgOrPng && isLt3M) {
      setThumbnailFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setThumbnailSrc(e.target.result)
      }
      reader.readAsDataURL(file)
    }
    return false
  }

  const onSubmit = (values) => {
    form
      .validateFields()
      .then((values) => {
        setLoading(true)
        let formData = new FormData()
        formData.append('xExperienceId', activeItem.xExperienceId)
        formData.append('mediaType', 'scan')
        formData.append('url', values.url)
        formData.append('displayName', values.displayName)
        formData.append('store', experienceInfo.store)
        formData.append('experienceName', experienceInfo.name)

        // If there's a thumbnail image file, add it
        if (thumbnailFile) {
          formData.append('thumbnail', thumbnailFile)
        } else if (
          thumbnailSrc &&
          !thumbnailSrc.startsWith('data:') &&
          thumbnailSrc !== ''
        ) {
          // If thumbnailSrc is a URL (not a data URL), include it in the data
          formData.append('thumbnailUrl', thumbnailSrc)
        }

        // Add insert position info if adding before/after
        if (activeItem.actionType === 'add' && activeItem.insertPosition) {
          formData.append('insertPosition', activeItem.insertPosition)
          formData.append(
            'referenceOrderNumber',
            activeItem.referenceOrderNumber
          )
        }

        const scanId = values.scanId
        let url = config.raUrl + `/admin/media/${scanId}/`
        let requestType = 'put'
        if (activeItem.actionType === 'add') {
          url = config.raUrl + '/admin/media/create/'
          requestType = 'post'
        }

        axios[requestType](url, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: headers.Authorization,
          },
        })
          .then((res) => {
            setLoading(false)
            setVisible(false)
            setActiveItem(null)
            setThumbnailSrc(null)
            setThumbnailFile(null)
            form.resetFields()
            setScans(res.data)
            message.success('Scan updated!')
          })
          .catch((err) => {
            setLoading(false)
            console.log('error caught: ', err)
            message.error('Something went wrong!')
          })
      })
      .catch((errorInfo) => {
        console.log(errorInfo)
      })
  }

  const submitDelete = () => {
    axios
      .delete(config.raUrl + `/admin/media/${activeItem.id}/`, {
        headers,
        data: {
          mediaType: 'scan',
          xExperienceId: activeItem.xExperienceId,
        },
      })
      .then((res) => {
        setVisible(false)
        setActiveItem(null)
        setThumbnailSrc(null)
        setThumbnailFile(null)
        form.resetFields()
        setScans(res.data)
        message.success('Scan deleted!')
      })
      .catch((err) => {
        console.log('error caught: ', err)
        message.error('Something went wrong!')
      })
  }

  return (
    <Modal
      title={activeItem?.actionType === 'add' ? 'Add Scan' : 'Edit Scan'}
      centered
      visible={visible}
      onCancel={onCancel}
      width={500}
      destroyOnClose={true}
      footer={
        activeItem?.actionType === 'add'
          ? [
              <Button key="2" onClick={onCancel}>
                Cancel
              </Button>,
              <Button
                key="3"
                type="primary"
                onClick={onSubmit}
                loading={loading}
              >
                Save
              </Button>,
            ]
          : [
              <Popconfirm
                key="1"
                placement="top"
                title="Are you sure?"
                onConfirm={submitDelete}
                okText="Delete"
                cancelText="Cancel"
              >
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>,
              <Button key="2" onClick={onCancel}>
                Cancel
              </Button>,
              <Button
                key="3"
                type="primary"
                onClick={onSubmit}
                loading={loading}
              >
                Save
              </Button>,
            ]
      }
    >
      <Form name="scanItem" layout={'vertical'} autoComplete="off" form={form}>
        <Form.Item name="scanId" noStyle>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item
          label="Thumbnail"
          name="thumbnail"
          className="scanItemThumbnail"
          valuePropName="file"
          getValueFromEvent={(e) => e.file}
        >
          <Upload
            name="thumbnail"
            listType="picture-card"
            className="avatar-uploader"
            showUploadList={false}
            beforeUpload={beforeUpload}
          >
            {thumbnailSrc ? (
              <img
                src={thumbnailSrc}
                alt="thumbnail"
                style={{ width: '100%' }}
              />
            ) : (
              uploadButton
            )}
          </Upload>
        </Form.Item>
        <Form.Item
          label="URL"
          name="url"
          className="inputFormItem"
          rules={[
            {
              required: true,
              message: 'Please input a URL!',
            },
          ]}
        >
          <Input placeholder="Enter scan URL" />
        </Form.Item>
        <Form.Item
          label="Display Name"
          name="displayName"
          className="inputFormItem"
          rules={[
            {
              required: true,
              message: 'Please input a display name!',
            },
          ]}
        >
          <Input placeholder="Enter display name" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
export default ScanItemModal
